import { webApi } from '@mi-front/api-clients';
import { useParamDrivenValue } from '@mi-front/core';
import { useEffect, useMemo } from 'react';
import { isVideoEditorProjectRenderCompleted } from '../lib/is-video-editor-project-render-completed';
import { VIDEO_EDITIONS_PROJECT_GROUP_QUERY_PARAM } from '../lib/video-editions-project-group-query-param';

export type VideoEditionsProjectCategoryTab = 'draft' | 'generated';

const VALID_TABS: VideoEditionsProjectCategoryTab[] = ['draft', 'generated'];

/**
 * Splits video editor projects into drafts (not fully rendered) vs completed renders, syncs the active group
 * with the URL (`veProjectGroup`), and corrects the URL when the current group has no items but the other does.
 */
export function useVideoEditionsProjectCategoryTabs(
  projects: webApi.EnrichedVideoEditorProjectListItemDto[] | undefined,
) {
  const { value: categoryRaw, setValue: setCategory } = useParamDrivenValue({
    param: VIDEO_EDITIONS_PROJECT_GROUP_QUERY_PARAM,
    validValues: VALID_TABS,
    defaultValue: 'draft',
  });

  const category = categoryRaw as VideoEditionsProjectCategoryTab;

  const { draftProjects, generatedProjects } = useMemo(() => {
    const list = projects ?? [];
    const draft: webApi.EnrichedVideoEditorProjectListItemDto[] = [];
    const generated: webApi.EnrichedVideoEditorProjectListItemDto[] = [];
    for (const p of list) {
      if (isVideoEditorProjectRenderCompleted(p)) {
        generated.push(p);
      } else {
        draft.push(p);
      }
    }
    return { draftProjects: draft, generatedProjects: generated };
  }, [projects]);

  useEffect(() => {
    if (!projects?.length) return;
    if (category === 'draft' && draftProjects.length === 0 && generatedProjects.length > 0) {
      setCategory('generated');
    } else if (
      category === 'generated' &&
      generatedProjects.length === 0 &&
      draftProjects.length > 0
    ) {
      setCategory('draft');
    }
  }, [projects, draftProjects.length, generatedProjects.length, setCategory, category]);

  return { draftProjects, generatedProjects };
}
